import { useEffect, useMemo, useState } from 'react';
import {
  ShoppingCart, Repeat, Gift, Wallet, Receipt, CircleCheck, Trash2, Plus,
  Sparkles, TriangleAlert, Package,
} from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, NativeSelect } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Checkbox } from '@/components/ui/Checkbox';
import { ApiError } from '@/lib/api/client';
import { useBillingSettings } from '@/hooks/useBillingSettings';
import SafetyAlertBanner, { type SafetyAlertLike } from '@/components/SafetyAlertBanner';
// Reused, not reimplemented: a plain <a href> carries no Authorization header
// and its Accept: text/html is not proxied through to the backend in dev.
// See features/billing/api.ts's own comment.
import { openBillPdf } from '@/features/billing/api';
import { PrintBillButton } from '@/features/printing/PrintBillButton';
import { ScheduleBadge } from '../ScheduleBadge';
import { useInventory } from '../useInventory';
import { useDispensingSettings } from '../useNearExpiry';
import { NearExpiryBadge, LaterBatchPanel, batchSelectionSummary } from '../BatchSelectionPanel';
import { pickFefoBatch, unresolvedLaterBatches, applyBatchSelectionReason, isNearExpiry } from '../batchSelection';
import {
  useLoyaltySettings, useLoyaltyQuote, useRepeatMedicines,
  useCreateOutpatientSale, useCustomer,
} from '../useOutpatient';
import CustomerPanel from './CustomerPanel';
import PrescriptionPanel from './PrescriptionPanel';
import {
  Empty, QtyStepper, StepRail, TotalLine, PaymentModePicker, PaymentStatusPicker,
  money, shortDate, type Step,
} from './ui';
import type {
  CounterCustomer, DrugSchedule, InventoryItem, OutpatientPrescription, OutpatientSaleResult,
  PaymentMode, RepeatMedicine,
} from '../types';

const isExpired = (d: string | null | undefined) => !!d && new Date(d) < new Date();
const SCHEDULED: DrugSchedule[] = ['H', 'H1', 'X'];


interface CartLine {
  inventory_id: number;
  medicine_id: number;
  name: string;
  qty: number;
  mrp: number;
  gst: number;
  discount: number;
  schedule: DrugSchedule;
  batch: string;
  expiry: string;
  available: number;
  /** Required by the sale engine when this line sells a later-expiry batch
   * while an earlier one could cover it — see ../batchSelection.ts. */
  batch_selection_reason?: string;
  batch_selection_remarks?: string;
}

/**
 * The walk-in pharmacy counter.
 *
 * Deliberately a separate screen from Dispensing rather than a mode of it.
 * Dispensing starts from "pick an approved prescription" and every field on it
 * is anchored to that; a counter sale starts from "who is this person?" and
 * has a discount, an outside prescription and points that make no sense on the
 * other. Forcing both into one screen would mean half the fields are
 * meaningless half the time.
 *
 * What it is NOT is a separate billing system. The sale posts to
 * routes/pharmacy-outpatient.js, which sells through the same backend engine
 * (utils/pharmacySale.js) as Dispensing — same stock locking, same GST, same
 * consecutive invoice series, same bills/bill_items tables — so a counter
 * purchase lands on the patient's ordinary billing record and shows up in
 * Patient Records → Billing next to their OPD and IP bills.
 *
 * ── Layout ──
 * One column until a customer is chosen, two after. An empty right-hand column
 * waiting for a cart is the most common way a till screen reads as broken on
 * first load, and it also buries the one thing the operator has to do first.
 * Once a customer exists the sale splits: who and what on the left, cart and
 * money on the right, with the money panel sticky so the payable never scrolls
 * out of reach of the person holding the cash.
 *
 * ── The pricing order, which is the part that is easy to get wrong ──
 *     gross    = qty × MRP
 *     discount = gross × discount%
 *     net      = gross − discount
 *     GST      = net × GST%        ← GST is charged AFTER the discount
 *     payable  = Σnet + ΣGST − loyalty
 * Mirrored here only so the operator sees a live total; the server recomputes
 * every figure from its own inventory rows and never trusts these.
 */
export default function OutpatientTab() {
  const { gstEnabled, gstPercentageOptions } = useBillingSettings();
  const { near_expiry_days: nearExpiryDays, batch_selection_reasons: batchReasons } = useDispensingSettings();

  const [customer, setCustomer] = useState<CounterCustomer | null>(null);
  const [prescription, setPrescription] = useState<OutpatientPrescription | null>(null);
  const [items, setItems] = useState<CartLine[]>([]);
  const [payMode, setPayMode] = useState<PaymentMode>('Cash');
  const [payStatus, setPayStatus] = useState<'Paid' | 'Pending'>('Paid');
  const [redeemInput, setRedeemInput] = useState('');
  const [sendReceipt, setSendReceipt] = useState(true);
  const [formError, setFormError] = useState('');
  const [confirmed, setConfirmed] = useState<OutpatientSaleResult | null>(null);
  const [advancedBatch, setAdvancedBatch] = useState(false);

  const inventoryQuery = useInventory({ channel: 'outpatient' });
  const loyaltyQuery = useLoyaltySettings();
  const customerQuery = useCustomer(customer?.id ?? null);
  const repeatQuery = useRepeatMedicines(customer?.id ?? null);
  const createSale = useCreateOutpatientSale();
  // The fresh detail fetch wins over the search row the customer was picked
  // from: a death recorded while this counter screen sat open must still stop
  // the sale here, not only as a 409 from the server after "Take payment".
  const customerDeceased = !!(customerQuery.data?.deceased ?? customer?.deceased);

  const inventory = inventoryQuery.data ?? [];
  const loyaltySettings = loyaltyQuery.data;
  const loyaltyOn = !!loyaltySettings?.enabled;

  // ── Totals ─────────────────────────────────────────────────────
  const gross = items.reduce((s, i) => s + i.qty * i.mrp, 0);
  const discount = items.reduce((s, i) => s + (i.qty * i.mrp * i.discount) / 100, 0);
  const net = gross - discount;
  const gstAmt = gstEnabled
    ? items.reduce((s, i) => {
        const lineNet = i.qty * i.mrp * (1 - i.discount / 100);
        return s + (lineNet * i.gst) / 100;
      }, 0)
    : 0;
  const payableBeforePoints = net + gstAmt;

  const quoteQuery = useLoyaltyQuote(customer?.id ?? null, payableBeforePoints, loyaltyOn);
  const quote = quoteQuery.data;
  const maxPoints = quote?.max_points ?? 0;
  const pointValue = quote?.point_value ?? loyaltySettings?.point_value ?? 1;

  const redeemPoints = Math.max(0, Math.min(maxPoints, parseInt(redeemInput, 10) || 0));
  const loyaltyDiscount = redeemPoints * pointValue;
  const total = Math.max(0, payableBeforePoints - loyaltyDiscount);

  const cartHasScheduled = items.some((i) => SCHEDULED.includes(i.schedule));
  const prescriptionBlocks = cartHasScheduled && (!prescription || prescription.skipped);

  // FEFO — one entry per medicine, defaulting to the batch expiring soonest.
  // Identical to the Dispensing tab's rule, and for the same reason: a later
  // batch is never sold while an earlier one still has stock unless the
  // operator picks it deliberately ("Pick a specific batch") and gives a
  // reason, which the sale engine records on the bill line.
  const dispensable = useMemo(
    () => inventory.filter((m) => m.quantity_available > 0 && !isExpired(m.expiry_date)),
    [inventory]
  );
  const fefoGroups = useMemo(() => {
    const groups: Record<number, InventoryItem> = {};
    dispensable.forEach((m) => {
      if (!groups[m.medicine_id] || new Date(m.expiry_date) < new Date(groups[m.medicine_id]!.expiry_date)) {
        groups[m.medicine_id] = m;
      }
    });
    return groups;
  }, [dispensable]);

  // `fefo` — the operator picked a MEDICINE (the dropdown, "Bought before"),
  // not a batch, so it is re-resolved to the earliest-expiring batch that
  // covers the whole quantity, net of what the cart already holds. The
  // "specific batch" picker passes fefo=false and keeps the batch chosen.
  const addFromInventory = (invId: string, qty = 1, fefo = false) => {
    const picked = inventory.find((m) => m.id === parseInt(invId, 10));
    if (!picked) return;
    const med = fefo
      ? pickFefoBatch(dispensable.filter((m) => m.medicine_id === picked.medicine_id), qty, items) ?? picked
      : picked;
    // The medicine's own stored rate is only a valid starting value if the
    // admin actually offers it — otherwise the <select> would render its first
    // option while the line quietly kept an unlisted rate, and the dropdown and
    // the total would disagree. Same guard DispensingTab.tsx applies.
    const medGst = parseFloat(med.gst_percent) || 12;
    const validGst = gstPercentageOptions.includes(medGst) ? medGst : (gstPercentageOptions[0] ?? 0);
    setItems((prev) => {
      // A second scan of the same medicine bumps the quantity rather than
      // adding a duplicate line — at a counter, "two more of those" is the
      // normal correction, and two lines for one batch make the bill confusing
      // and the return screen ambiguous.
      const existing = prev.findIndex((l) => l.inventory_id === med.id);
      if (existing >= 0) {
        return prev.map((l, idx) => (idx === existing ? { ...l, qty: Math.min(l.qty + qty, l.available) } : l));
      }
      return [...prev, {
        inventory_id: med.id,
        medicine_id: med.medicine_id,
        name: `${med.generic_name} ${med.strength || ''}`.trim(),
        qty: Math.min(qty, med.quantity_available) || 1,
        mrp: parseFloat(med.mrp),
        gst: gstEnabled ? validGst : 0,
        discount: 0,
        schedule: med.schedule,
        batch: med.batch_number,
        expiry: med.expiry_date,
        available: med.quantity_available,
      }];
    });
  };

  /**
   * Adds a medicine this customer has bought before. The backend already
   * resolved it to today's FEFO batch, so this is a single click — which is the
   * entire reason a counter sale is linked to a patient record rather than
   * being an anonymous till transaction.
   */
  const addRepeat = (r: RepeatMedicine) => {
    if (!r.inventory_id) return;
    addFromInventory(String(r.inventory_id), r.last_quantity || 1, true);
  };

  // Lines on a later-expiry batch while an earlier one could cover them —
  // only reachable through the "specific batch" picker. The sale engine
  // refuses these without batch_selection_reason, so the banner below asks
  // before payment rather than after.
  const unresolvedLater = useMemo(() => unresolvedLaterBatches(items, dispensable), [items, dispensable]);

  const applyLaterBatchReason = (ids: Set<number>, reason: string, remarks: string) =>
    setItems((prev) => applyBatchSelectionReason(prev, ids, reason, remarks));

  // "Use batch X": move the line onto the earlier batch — or into the line
  // already on it, since the counter keeps one line per batch.
  const switchToEarlierBatch = (line: CartLine, earlierId: number) => {
    const earlier = inventory.find((m) => m.id === earlierId);
    if (!earlier) return;
    setItems((prev) => {
      const existing = prev.find((l) => l.inventory_id === earlier.id);
      if (existing) {
        return prev
          .filter((l) => l !== line)
          .map((l) => (l === existing ? { ...l, qty: Math.min(l.qty + line.qty, l.available) } : l));
      }
      return prev.map((l) => (l === line
        ? { ...l, inventory_id: earlier.id, batch: earlier.batch_number, expiry: earlier.expiry_date, available: earlier.quantity_available, mrp: parseFloat(earlier.mrp) }
        : l));
    });
  };

  const updateLine = (idx: number, patch: Partial<CartLine>) =>
    setItems((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));

  // If the admin removes a GST percentage that a cart line is currently on,
  // fall back to the first configured option rather than silently keeping an
  // unlisted rate.
  useEffect(() => {
    const options = gstPercentageOptions.map(String);
    if (!options.length) return;
    setItems((prev) => prev.map((l) => (options.includes(String(l.gst)) ? l : { ...l, gst: parseFloat(options[0] as string) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gstPercentageOptions]);

  // Typing more points than the cap allows is corrected in place rather than
  // rejected on save — the cap moves as the cart changes, so a number that was
  // valid a moment ago can stop being valid without the operator touching it.
  useEffect(() => {
    const wanted = parseInt(redeemInput, 10) || 0;
    if (wanted > maxPoints) setRedeemInput(maxPoints ? String(maxPoints) : '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxPoints]);

  const reset = () => {
    setCustomer(null);
    setPrescription(null);
    setItems([]);
    setRedeemInput('');
    setFormError('');
    setPayMode('Cash');
    setPayStatus('Paid');
  };

  const save = () => {
    if (!customer || !items.length) return;
    if (prescriptionBlocks) {
      setFormError("This cart contains a Schedule H/H1/X medicine — add the prescriber's details before selling it.");
      return;
    }
    if (unresolvedLater.length) {
      setFormError('A later-expiry batch is in the cart while an earlier one is available — give a reason or switch batch first.');
      return;
    }
    setFormError('');
    createSale.mutate(
      {
        patient_id: customer.id,
        outpatient_prescription_id: prescription?.id,
        items: items.map((i) => ({
          inventory_id: i.inventory_id,
          name: i.name,
          quantity: i.qty,
          gst_percent: gstEnabled ? i.gst : 0,
          discount_percent: i.discount || undefined,
          batch_selection_reason: i.batch_selection_reason,
          batch_selection_remarks: i.batch_selection_remarks,
        })),
        payment_mode: payMode,
        payment_status: payStatus,
        redeem_points: redeemPoints || undefined,
        send_receipt: sendReceipt,
      },
      {
        onSuccess: (sale) => { setConfirmed(sale); reset(); },
      }
    );
  };

  const saleError = createSale.error instanceof ApiError ? createSale.error.message : null;
  const repeats = repeatQuery.data ?? [];
  const alerts: SafetyAlertLike[] = confirmed?.alerts ?? [];
  const balance = customerQuery.data?.loyalty?.points_balance ?? customer?.points_balance ?? 0;
  const expiringSoon = customerQuery.data?.loyalty_expiring;

  // ── Progress ───────────────────────────────────────────────────
  // Purely derived from the sale's own state; there is no separate wizard
  // position to keep in sync, so the rail can never disagree with the screen.
  const steps: Step[] = [
    { id: 'customer', label: 'Customer', state: customer ? 'done' : 'active' },
    { id: 'rx', label: 'Prescription', state: prescription ? 'done' : customer ? 'active' : 'todo' },
    { id: 'cart', label: 'Medicines', state: items.length ? 'done' : customer ? 'active' : 'todo' },
    ...(loyaltyOn ? [{ id: 'points', label: 'Points', state: (items.length ? 'active' : 'todo') as Step['state'] }] : []),
    { id: 'pay', label: 'Payment', state: items.length ? 'active' : 'todo' },
  ];

  return (
    <div className="space-y-4">
      <StepRail steps={steps} />

      {/* ── Just-completed sale ─────────────────────────────────── */}
      {confirmed && (
        <Card className="border-success-soft">
          <div className="flex flex-wrap items-start gap-3">
            <span className="grid h-10 w-10 shrink-0 place-content-center rounded-full bg-success-soft text-success-ink">
              <CircleCheck size={20} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold">
                Bill {confirmed.bill_number} · {money(confirmed.total_amount)}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                <Badge variant={confirmed.payment_status === 'Paid' ? 'success' : 'warn'}>
                  {confirmed.payment_status === 'Paid' ? 'Paid' : 'Pending'}
                </Badge>
                {confirmed.points_earned > 0 && (
                  <Badge variant="warn" className="inline-flex items-center gap-1">
                    <Sparkles size={10} /> +{confirmed.points_earned} points earned
                  </Badge>
                )}
                {confirmed.points_redeemed > 0 && (
                  <Badge variant="info">−{confirmed.points_redeemed} points used</Badge>
                )}
              </div>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Button size="sm" variant="ghost" onClick={() => { void openBillPdf(confirmed.id); }}>
                <Receipt size={13} /> Invoice
              </Button>
              <PrintBillButton billId={confirmed.id} />
              <Button size="sm" onClick={() => setConfirmed(null)}>Next sale</Button>
            </div>
          </div>
          {/* Advisory only — computed after the sale committed, exactly as on the
              Dispensing path. A walk-in who has consulted here before has real
              allergies on record, and this is where they matter. */}
          <SafetyAlertBanner alerts={alerts} className="mt-3" />
        </Card>
      )}

      {/* ── No customer yet: one centred column, nothing else ───── */}
      {!customer ? (
        <div className="mx-auto w-full max-w-xl">
          <CustomerPanel
            customer={null}
            loyaltyEnabled={loyaltyOn}
            onSelect={(c) => { setCustomer(c); setPrescription(null); setConfirmed(null); }}
            onClear={reset}
          />
        </div>
      ) : (
        <div className="grid items-start gap-4 min-[1100px]:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
          {/* ── Left: who, and why ─────────────────────────────── */}
          <div className="space-y-4">
            <CustomerPanel
              customer={customer}
              loyaltyEnabled={loyaltyOn}
              onSelect={(c) => { setCustomer(c); setPrescription(null); setConfirmed(null); }}
              onClear={reset}
              deceased={customerDeceased}
            />

            <PrescriptionPanel
              customer={customer}
              prescription={prescription}
              cartHasScheduled={cartHasScheduled}
              onCaptured={setPrescription}
              onClear={() => setPrescription(null)}
            />

            {repeats.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle><Repeat size={15} /> Bought before</CardTitle>
                  <Badge variant="neutral">{repeats.length}</Badge>
                </CardHeader>
                <p className="-mt-2 mb-3 text-[11.5px] text-ink-muted">
                  Everything {customer.name.split(' ')[0]} has bought here — from consultations and
                  counter visits alike — already matched to the batch on the shelf today.
                </p>
                <div className="space-y-1.5">
                  {repeats.map((r) => (
                    <div key={`${r.medicine_id}-${r.description}`} className="flex items-center gap-2.5 rounded-lg border border-border px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate text-[12.5px] font-semibold">{r.description}</span>
                          {r.schedule !== 'OTC' && <ScheduleBadge schedule={r.schedule} />}
                        </div>
                        <div className="mt-0.5 text-[11px] text-ink-muted">
                          {r.times_purchased}× · last {shortDate(r.last_purchased_at)}
                          {r.inventory_id ? ` · ${money(r.mrp)}` : ''}
                        </div>
                      </div>
                      {r.inventory_id ? (
                        <Button size="sm" variant="ghost" className="shrink-0" onClick={() => addRepeat(r)}>
                          <Plus size={13} /> {r.last_quantity}
                        </Button>
                      ) : (
                        <Badge variant="neutral" className="shrink-0">Out of stock</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          {/* ── Right: what, and how much ──────────────────────── */}
          <div className="space-y-4 min-[1100px]:sticky min-[1100px]:top-4">
            <Card>
              <CardHeader>
                <CardTitle><ShoppingCart size={15} /> Medicines</CardTitle>
                {items.length > 0 && (
                  <Badge variant="neutral">{items.length} line{items.length === 1 ? '' : 's'}</Badge>
                )}
              </CardHeader>

              {(saleError || formError) && (
                <div className="mb-3 flex gap-2.5 rounded-lg bg-danger-soft px-3 py-2.5 text-[12px] text-danger-ink">
                  <TriangleAlert size={15} className="mt-px shrink-0" />
                  <p className="leading-relaxed">{saleError || formError}</p>
                </div>
              )}

              <NativeSelect
                defaultValue=""
                aria-label="Add a medicine"
                onChange={(e) => { if (e.target.value) { addFromInventory(e.target.value, 1, true); e.target.value = ''; } }}
              >
                <option value="">+ Add a medicine…</option>
                {Object.values(fefoGroups).map((m) => (
                  <option key={m.medicine_id} value={m.id}>
                    {isNearExpiry(m.expiry_date, nearExpiryDays) ? '⏳ ' : ''}{m.generic_name} {m.strength} {m.schedule !== 'OTC' ? `[${m.schedule}]` : ''} — ₹{parseFloat(m.mrp).toFixed(2)} · {m.quantity_available} left
                  </option>
                ))}
              </NativeSelect>
              <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
                <p className="text-[11px] text-ink-muted">
                  Earliest-expiring batch first, so nothing is left to expire on the shelf.
                  {' '}⏳ = expires within {nearExpiryDays} days.
                </p>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => setAdvancedBatch((a) => !a)}>
                  {advancedBatch ? '▲ Hide' : '▼'} Pick a specific batch
                </Button>
              </div>
              {advancedBatch && (
                <NativeSelect
                  className="mt-1.5"
                  defaultValue=""
                  aria-label="Add a specific batch"
                  onChange={(e) => { if (e.target.value) { addFromInventory(e.target.value); e.target.value = ''; } }}
                >
                  <option value="">Select exact batch… (a later batch needs a reason)</option>
                  {dispensable.map((m) => (
                    <option key={m.id} value={m.id}>
                      {isNearExpiry(m.expiry_date, nearExpiryDays) ? '⏳ ' : ''}{m.generic_name} {m.strength} — {m.batch_number} (exp {shortDate(m.expiry_date)}, {m.quantity_available} left)
                    </option>
                  ))}
                </NativeSelect>
              )}

              <div className="mt-3.5">
                {items.length === 0 ? (
                  <Empty label="Nothing in the cart yet" hint="Add a medicine above, or pick one from “Bought before”." />
                ) : (
                  <div className="divide-y divide-line">
                    {items.map((item, idx) => {
                      const lineGross = item.qty * item.mrp;
                      const lineNet = lineGross * (1 - item.discount / 100);
                      const lowStock = item.qty >= item.available;
                      return (
                        <div key={item.inventory_id} className="py-2.5 first:pt-0">
                          <div className="flex items-start gap-2">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-1.5">
                                <span className="truncate text-[13px] font-semibold">{item.name}</span>
                                {item.schedule !== 'OTC' && <ScheduleBadge schedule={item.schedule} />}
                                <NearExpiryBadge expiry={item.expiry} nearExpiryDays={nearExpiryDays} />
                                {item.batch_selection_reason && (
                                  <Badge variant="warn" title={`Later-expiry batch: ${batchSelectionSummary(item, batchReasons)}`}>↷ Later batch</Badge>
                                )}
                              </div>
                              <div className="mt-0.5 text-[11px] text-ink-muted">
                                {money(item.mrp)} each · batch {item.batch} · exp {shortDate(item.expiry)}
                                {item.batch_selection_reason && <> · {batchSelectionSummary(item, batchReasons)}</>}
                              </div>
                            </div>
                            <div className="shrink-0 text-right">
                              <div className="text-[13px] font-semibold tabular-nums">{money(lineNet)}</div>
                              {item.discount > 0 && (
                                <div className="text-[11px] text-ink-muted line-through tabular-nums">{money(lineGross)}</div>
                              )}
                            </div>
                            <button
                              type="button"
                              aria-label={`Remove ${item.name}`}
                              onClick={() => setItems((prev) => prev.filter((_, i) => i !== idx))}
                              className="mt-px shrink-0 rounded-md p-1 text-ink-muted transition-colors hover:bg-danger-soft hover:text-danger-ink"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>

                          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
                            <QtyStepper
                              value={item.qty}
                              max={item.available}
                              onChange={(v) => updateLine(idx, { qty: v })}
                            />
                            <label className="flex items-center gap-1.5">
                              <span className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">Disc</span>
                              <div className="relative">
                                <Input
                                  type="number"
                                  className="h-7 w-16 pr-5 text-[13px] tabular-nums"
                                  value={item.discount}
                                  min={0}
                                  max={100}
                                  onChange={(e) => updateLine(idx, { discount: Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)) })}
                                />
                                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-ink-muted">%</span>
                              </div>
                            </label>
                            {gstEnabled && (
                              <label className="flex items-center gap-1.5">
                                <span className="text-[10.5px] font-semibold uppercase tracking-wide text-ink-muted">GST</span>
                                <NativeSelect
                                  className="h-7 w-[74px] py-0 text-[13px]"
                                  value={item.gst}
                                  onChange={(e) => updateLine(idx, { gst: parseFloat(e.target.value) || 0 })}
                                >
                                  {gstPercentageOptions.map((g) => <option key={g} value={g}>{g}%</option>)}
                                </NativeSelect>
                              </label>
                            )}
                            {lowStock && (
                              <Badge variant="warn" className="inline-flex items-center gap-1">
                                <Package size={10} /> All {item.available} taken
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <LaterBatchPanel
                className="mt-3"
                issues={unresolvedLater}
                reasons={batchReasons}
                onApply={applyLaterBatchReason}
                onUseEarlier={switchToEarlierBatch}
              />
            </Card>

            {items.length > 0 && (
              <Card>
                {/* ── Points ──────────────────────────────────── */}
                {loyaltyOn && (
                  <div className="mb-4 rounded-lg border border-border p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 text-[13px] font-semibold">
                        <Gift size={14} /> Loyalty points
                      </div>
                      <Badge variant="warn">{balance} available</Badge>
                    </div>

                    {expiringSoon && expiringSoon.points > 0 && (
                      <div className="mb-2 rounded-md bg-warn-soft px-2.5 py-1.5 text-[11px] text-caution-ink">
                        {expiringSoon.points} point{expiringSoon.points === 1 ? '' : 's'} expire on{' '}
                        {shortDate(expiringSoon.next_expiry)} — worth using today.
                      </div>
                    )}

                    {maxPoints > 0 ? (
                      <>
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="relative">
                            <Input
                              type="number"
                              className="w-24 pr-9 tabular-nums"
                              min={0}
                              max={maxPoints}
                              value={redeemInput}
                              placeholder="0"
                              onChange={(e) => setRedeemInput(e.target.value)}
                            />
                            <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-ink-muted">pts</span>
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => setRedeemInput(String(maxPoints))}>
                            Use all {maxPoints}
                          </Button>
                          {redeemPoints > 0 && (
                            <span className="text-[13px] font-semibold text-success-ink">− {money(loyaltyDiscount)}</span>
                          )}
                        </div>
                        <p className="mt-1.5 text-[11px] text-ink-muted">
                          Up to <strong>{maxPoints}</strong> ({money(maxPoints * pointValue)}) on this bill
                          {quote?.limiting_factor ? <> — limited by <strong>{quote.limiting_factor}</strong>.</> : '.'}
                        </p>
                      </>
                    ) : (
                      <p className="text-[11.5px] text-ink-muted">
                        {quote?.limiting_factor || 'No points can be used on this bill yet.'}
                      </p>
                    )}

                    {quote?.would_earn ? (
                      <p className="mt-2 flex items-center gap-1.5 border-t border-line pt-2 text-[11.5px] text-ink-muted">
                        <Sparkles size={12} className="text-teal" />
                        Earns about <strong>{quote.would_earn}</strong> point{quote.would_earn === 1 ? '' : 's'}.
                      </p>
                    ) : null}
                  </div>
                )}

                {/* ── Totals ──────────────────────────────────── */}
                <div className="rounded-lg bg-canvas p-3">
                  <TotalLine label="Subtotal" value={gross} />
                  {discount > 0 && <TotalLine label="Discount" value={discount} tone="credit" />}
                  {gstEnabled && <TotalLine label="GST" value={gstAmt} hint="on the discounted value" />}
                  {loyaltyDiscount > 0 && <TotalLine label={`Loyalty (${redeemPoints} pts)`} value={loyaltyDiscount} tone="credit" />}
                  <TotalLine label="To pay" value={total} tone="grand" />
                </div>

                {/* ── Payment ─────────────────────────────────── */}
                {/* The same two controls the Returns screen uses for a refund —
                    see ui.tsx. Money in and money out are the same decision. */}
                <div className="mt-3.5 space-y-3">
                  <PaymentModePicker value={payMode} onChange={setPayMode} />
                  <PaymentStatusPicker
                    value={payStatus}
                    onChange={setPayStatus}
                    paidHint="Money taken now"
                    pendingHint="Owed — collect it later"
                  />
                </div>

                <label className="mt-3 flex cursor-pointer items-center gap-2 text-[12px]">
                  <Checkbox checked={sendReceipt} onCheckedChange={(v) => setSendReceipt(v === true)} />
                  <span>Send an SMS receipt to {customer.mobile}</span>
                </label>

                {customerDeceased && (
                  <div className="mt-3 flex gap-2.5 rounded-lg bg-danger-soft px-3 py-2.5 text-[12px] text-danger-ink">
                    <TriangleAlert size={15} className="mt-px shrink-0" />
                    <p className="leading-relaxed">
                      {customer.name} is recorded as deceased — a new sale cannot be made on this record.
                      Returns of earlier purchases are still allowed on the Sales &amp; Returns tab.
                    </p>
                  </div>
                )}

                {prescriptionBlocks && (
                  <div className="mt-3 flex gap-2.5 rounded-lg bg-danger-soft px-3 py-2.5 text-[12px] text-danger-ink">
                    <TriangleAlert size={15} className="mt-px shrink-0" />
                    <p className="leading-relaxed">
                      A Schedule H/H1/X medicine is in the cart. Add the prescriber's details in step 2
                      before taking payment.
                    </p>
                  </div>
                )}

                <Button
                  variant="success"
                  full
                  size="lg"
                  className="mt-3.5"
                  disabled={createSale.isPending || items.length === 0 || prescriptionBlocks || unresolvedLater.length > 0 || customerDeceased}
                  loading={createSale.isPending}
                  onClick={save}
                >
                  {!createSale.isPending && <Wallet size={16} />}
                  {createSale.isPending ? 'Saving…'
                    : customerDeceased ? 'Sale blocked — patient recorded as deceased'
                    : unresolvedLater.length > 0 ? 'Resolve batch choice to continue'
                    : `Take payment · ${money(total)}`}
                </Button>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
